import { storeData, getData } from '../redux/store';

import jwt_decode from "jwt-decode";
import { Context } from '../context/Context';
import { useCallback, useContext, useMemo } from 'react';
import { useLocale } from '../localization/LocaleProvider';


// In-memory spegel av token
let inMemoryToken = null;
const PERSIST_KEY = 'token_v2';
const LEGACY_KEY = 'token';

// Sparar refreshToken + user i local storage
async function persistRefreshAndUser(user, refreshToken) {
  return storeData(PERSIST_KEY, { user: user, refreshToken: refreshToken });
}

// Migrerar legacy token från v1 till v2
async function maybeMigrateLegacyToken() {
  const legacy = await getData(LEGACY_KEY);
  if (legacy && legacy.refreshToken && legacy.user && legacy.user.ID) {
    await persistRefreshAndUser(legacy.user, legacy.refreshToken);
    await storeData(LEGACY_KEY, null); // städa bort
    return {
      user: legacy.user,
      refreshToken: legacy.refreshToken
    };
  }
  return null;
}

// Rensar all token-data från minne och local storage
async function clearAllTokenState() {
  inMemoryToken = null;
  await storeData(PERSIST_KEY, null);
  await storeData(LEGACY_KEY, null);
}

// promise-kedja som aldrig nollställs
let lastQueuePromise = Promise.resolve();

function enqueue(fn) {
  lastQueuePromise = lastQueuePromise.catch(err => {
    // Hantera fel i tidigare Promise-kedja
    // console.log('Error in previous API call:', err);
  }).then(() => fn());
  return lastQueuePromise;
}


// in-memory cache för getUser
const pendingFetches = new Map();

// refreshPromise kan ligga kvar skapat här också
let refreshPromise = null;

export function useApi() {

  const { locale, strings } = useLocale();
  const API_URL = process.env.EXPO_PUBLIC_API_URL;
  //const baseUrl = API_URL;
  const environment = process.env.NODE_ENV || 'development';
  const baseUrl = environment == 'production' ? 'https://adminapi.rolf.se' : 'https://adminapi.rolf.se:17654' // Development
  // const baseUrl = environment === 'production' ? 'https://adminapi.rolf.se' : API_URL;
  const appContext = useContext(Context);

  const safeToast = useCallback(() => {}, []);

  const request = (method, url, body, options = {}) => {
    // returnerar enqueue-promise:n direkt

    if (method === 'GET') {
      const key = url + JSON.stringify(options);  // du kan inkludera headers/body om du vill
      if (pendingFetches.has(key)) {
        // Returnera samma Promise som redan körs
        return pendingFetches.get(key);
      }
      // Dedupa endast samma GET, men blockera inte andra läsningar bakom global kö
      const p = (async () => {
        const token = await Api.validateToken(url);
        const controllerTimeout = setTimeout(() => {
          safeToast(
            'info',
            strings('Lång väntetid'),
            strings('Din förfrågan tar oväntat lång tid att behandla. Vi fortsätter att försöka.')
          );
        }, 10000);

        const requestOptions = {
          method,
          ...options,
          headers: {
            'Accept-Language': locale,
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token.authToken}`,
            ...(options.headers || {}),
          }
        };

        try {
          let response = await fetch(baseUrl + url, requestOptions);

          // Om auth-token hann gå ut mellan validateToken() och själva fetch:
          if (response.status === 401 && !requestOptions._retried) {
            try {
              // försök refresha och retraya exakt en gång
              await Api.refreshToken(await Api.getToken());
              const fresh = await Api.getToken();
              const retryOptions = {
                ...requestOptions,
                headers: {
                  ...requestOptions.headers,
                  Authorization: `Bearer ${fresh.authToken}`,
                },
                _retried: true,
              };
              response = await fetch(baseUrl + url, retryOptions);
            } catch (e) {
              // Misslyckad refresh: låt handleResponse ta vid (kan bli 401/403)
            }
          }

          return await Api.handleResponse(response);
        } finally {
          clearTimeout(controllerTimeout);
        }

      })()
        .finally(() => {
          // När GET:en är klar tar vi bort den ur pendingFetches
          pendingFetches.delete(key);
        });

      // Spara i pendingFetches innan vi kör
      pendingFetches.set(key, p);
      return p;
    }

    return enqueue(async () => {
      // 1) säkra token (awaita eventuell refresh)
      const token = await Api.validateToken(url);

      // 2) bygg fetch-options
      const controllerTimeout = setTimeout(() => {
        safeToast(
          'info',
          strings('Lång väntetid'),
          strings('Din förfrågan tar oväntat lång tid att behandla. Vi fortsätter att försöka.')
        );
      }, 10000);

      const headers = {
        'Accept-Language': locale,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token.authToken}`,
      };
      const requestOptions = { method, ...options, headers: { ...headers, ...options.headers } };
      if (body) requestOptions.body = JSON.stringify(body);

      try {
        let response = await fetch(baseUrl + url, requestOptions);

        if (response.status === 401 && !requestOptions._retried) {
          try {
            await Api.refreshToken(await Api.getToken());
            const fresh = await Api.getToken();
            const retryOptions = {
              ...requestOptions,
              headers: {
                ...requestOptions.headers,
                Authorization: `Bearer ${fresh.authToken}`,
              },
              _retried: true,
            };
            response = await fetch(baseUrl + url, retryOptions);
          } catch (e) {
            // refresh misslyckades -> handleResponse hanterar 401/403
          }
        }

        return await Api.handleResponse(response);
      } finally {
        clearTimeout(controllerTimeout);
      }
    });
  };

  const Api = useMemo(() => {

    return {
      baseUrl: baseUrl,
      registerDevicePushTokenAsync: async (token, deviceType) => {
        await Api.post('/registerDevicePushToken', { token: token, deviceType }).then(result => {

        })
      },
      unRegisterDevicePushTokenAsync: async (token, deviceType) => {
        Api.delete('/registerDevicePushToken', { token: token, deviceType }).then(result => { }).catch(e => { })
      },
      // Saves token to local storage
      setToken: async (token) => {
        if (!token || !token.refreshToken || !token.user || !token.user.ID) {
          throw new Error('Refusing to set invalid token payload');
        }

        // Persist ONLY refresh+user
        await persistRefreshAndUser(token.user, token.refreshToken);

        // Keep authToken in memory
        inMemoryToken = {
          user: token.user,
          refreshToken: token.refreshToken,
          authToken: token.authToken
        };

        return inMemoryToken;
      },
      // Get stored token from local storage
      getToken: async () => {
        // First try in-memory
        if (inMemoryToken && inMemoryToken.user && inMemoryToken.user.ID) {
          return inMemoryToken;
        }

        // Try v2
        let v2 = await getData(PERSIST_KEY);

        if (!v2) {
          // Try migrating legacy
          const migrated = await maybeMigrateLegacyToken();
          if (migrated) v2 = migrated;
        }

        if (v2 && v2.user && v2.user.ID && v2.refreshToken) {
          // Rehydrate into memory
          inMemoryToken = {
            user: v2.user,
            refreshToken: v2.refreshToken,
            // authToken kommer läsas fram via validateToken() -> refresh()
            authToken: inMemoryToken ? inMemoryToken.authToken : undefined
          };
          return inMemoryToken;
        }

        // No token found
        return null;
      },

      // Try to refresh authToken using refreshToken in storage.
      refreshToken: async (token) => {
        if (!token || !token.refreshToken || !token.user || !token.user.ID) {
          throw { status: 403, error: 'No valid token or user!' };
        }

        // Om refresh redan pågår, återanvänd samma Promise
        if (refreshPromise) {
          //console.log('Refresh already in progress, waiting for it to finish...');
          return refreshPromise;
        }

        // Skapa ny refresh-promise
        refreshPromise = (async () => {
          const requestOptions = {
            method: 'POST',
            headers: {
              'Accept-Language': locale,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ user: token.user, refreshToken: token.refreshToken }),
          };

          try {
            const response = await fetch(baseUrl + '/refreshToken', requestOptions);
            const newToken = await Api.handleResponse(response);

            if (!newToken?.authToken || !newToken?.refreshToken || !newToken?.user?.ID) {
              throw { status: 500, error: 'Invalid token payload' };
            }

            await Api.setToken(newToken);
            return newToken;

          } catch (err) {
            console.error('Error refreshing token:', err);

            if (err?.status === 401 || err?.status === 403) {
              appContext.dispatch({ type: 'logout', from: 'refreshToken' });
              await clearAllTokenState();
              throw err;
            }

            throw { offline: true, error: err?.error || 'Network error during refresh' };
          } finally {
            refreshPromise = null;
          }
        })();

        return refreshPromise;
      },
      // Checks if token has expired
      isTokenExpired: (token) => {
        let decodedToken;
        try {
          decodedToken = jwt_decode(token);
        } catch (e) {
          return true; // behandla som om token är ogiltig
        }
        let isExpired = true;
        if (decodedToken && decodedToken.exp) {
          const expirationDate = new Date(0);
          expirationDate.setUTCSeconds(decodedToken.exp);
          const buffer = 30 * 1000; // 30 seconds buffer
          isExpired = expirationDate.valueOf() < (new Date().valueOf() + buffer);
        }
        return isExpired;
      },
      // Validates token in storage and try to refresh if expired or not present.
      validateToken: async (req) => {
        try {
          if (refreshPromise) {
            //console.log('Refresh already in progress, waiting for it to finish...');
            return await refreshPromise;
          }
          const token = await Api.getToken();

          if (!token) {
            // en extra chans: ibland hinner storage hydrera precis efter
            await new Promise(r => setTimeout(r, 80));
            const t2 = await Api.getToken();
            if (!t2) {
              throw { status: 401, needsLogin: true, error: 'No token in storage' };
            }
            return t2;
          }

          if (!token.authToken) {
            // Har vi "refresh-material" (refreshToken + user.ID)? => försök refresha
            if (token.refreshToken && token.user?.ID) {
              return await Api.refreshToken(token);
            }
            // Annars kan vi inte refresha => needsLogin
            throw { status: 401, needsLogin: true, error: 'Missing authToken and no refresh material' };
          }

          if (Api.isTokenExpired(token.authToken)) {
            // (Valfritt: hoppa över lokal expiry-check på refreshToken om den inte är JWT)
            if (!(token.refreshToken && token.user?.ID)) {
              throw { status: 401, needsLogin: true, error: 'Expired authToken and no refresh material' };
            }
            // Om en refresh redan är igång, vänta på den
            if (refreshPromise) {
              return await refreshPromise;
            }
            return await Api.refreshToken(token);
          }

          return token;
        } catch (error) {
          console.log('Error validating token:', error);

          const isAuth = error?.needsLogin === true || error?.status === 401 || error?.status === 403;

          // 1) logga ut först – detta får inte blockeras av UI
          if (isAuth) {
            try {
              appContext.dispatch({ type: 'logout', from: 'validateToken' });
              await clearAllTokenState();
            } catch (e) {
              console.warn('Logout dispatch failed:', e);
            }
            safeToast('error', strings('Session ended'));
            console.error('Logging out due to error in validateToken:', error);
            throw { status: error?.status || 401, error: error?.error || 'Session ended' };
          }

          throw {
            status: error?.status || 500,
            error: error?.error || 'Request failed',
            offline: error?.offline || false,
          };
        }
      },
      clearToken: async () => {
        inMemoryToken = null;
        await clearAllTokenState();
      },
      // Login user and get a sessionId 
      login: (credentials) => {
        const requestOptions = {
          method: 'POST',
          headers: {
            'Accept-Language': locale,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(credentials)
        };
        return fetch(baseUrl + '/session', requestOptions).then(Api.handleResponse).then((sessionId) => {
          return sessionId;
        })
      },
      otp: (otp, sessionId) => {
        const requestOptions = {
          method: 'POST',
          headers: {
            'Accept-Language': locale,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ otp, sessionId })
        };
        return fetch(baseUrl + '/session/otp', requestOptions).then(Api.handleResponse).then((token) => {
          if (!token?.authToken || !token?.refreshToken || !token?.user?.ID) {
            throw { status: 500, error: 'Invalid token payload from server' };
          }
          return Api.setToken(token).then((token) => {
            return token;
          });
        })
      },
      sendOtp: (sessionId, media) => {
        const requestOptions = {
          method: 'POST',
          headers: {
            'Accept-Language': locale,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ sessionId, media })
        };
        return fetch(baseUrl + '/session/sendOtp', requestOptions).then(Api.handleResponse); // -> boolean
      },

      requestPasswordReset: (email) => {
        const requestOptions = {
          method: 'POST',
          headers: {
            'Accept-Language': locale,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(email)
        };
        return fetch(baseUrl + '/requestpass', requestOptions).then(Api.handleResponse).then((sessionId) => {
          return sessionId;
        })
      },
      checkRequestHash: (hash) => {
        const requestOptions = {
          method: 'POST',
          headers: {
            'Accept-Language': locale,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(hash)
        };
        return fetch(baseUrl + '/checkrequesthash', requestOptions).then(Api.handleResponse).then((result) => {
          return result;
        })
      },
      // Normal GET request

      get: (url, options) => request('GET', url, null, options),
      post: (url, body, options) => request('POST', url, body, options),
      put: (url, body, options) => request('PUT', url, body, options),
      delete: (url, body, options) => request('DELETE', url, body, options),
      download: async (url, options = {}) => {
        const token = await Api.validateToken(url);
        const accept = options.accept || 'text/csv';

        const requestOptions = {
          method: 'GET',
          headers: {
            'Accept-Language': locale,
            Accept: accept,
            Authorization: `Bearer ${token.authToken}`,
            ...(options.headers || {}),
          }
        };

        let response = await fetch(baseUrl + url, requestOptions);

        if (response.status === 401 && !requestOptions._retried) {
          await Api.refreshToken(await Api.getToken());
          const fresh = await Api.getToken();
          response = await fetch(baseUrl + url, {
            ...requestOptions,
            _retried: true,
            headers: {
              ...requestOptions.headers,
              Authorization: `Bearer ${fresh.authToken}`,
            }
          });
        }

        if (!response.ok) {
          const text = await response.text();
          let data;
          try {
            data = text ? JSON.parse(text) : {};
          } catch (e) {
            data = {};
          }
          const error = (data && (data.errorMessage || data.error || data.message)) || text || response.statusText;
          throw { status: response.status, error };
        }

        const arrayBuffer = await response.arrayBuffer();
        const disposition = response.headers.get('content-disposition') || '';
        const matchedFilename = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        const filename = options.filename || (matchedFilename ? matchedFilename[1].replace(/['"]/g, '') : 'export.csv');
        const contentType = response.headers.get('content-type') || 'text/csv; charset=utf-8';
        const blob = new Blob([arrayBuffer], { type: contentType });
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        anchor.rel = 'noopener';
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.setTimeout(() => {
          window.URL.revokeObjectURL(objectUrl);
        }, 1000);
        return { filename };
      },

      // Filter results from requests and checks of status is OK (HTTP 200)
      handleResponse: async (response) => {
        const text = await response.text();
        let data;
        try {
          data = text ? JSON.parse(text) : {};
        } catch (e) {
          data = {};
        }
        if (!response.ok) {
          const error = (data && (data.errorMessage || data.error || data.message)) || text || response.statusText;

          throw { status: response.status, error };
        }
        return data;
      },
      getLoggedInUser: async () => {
        const token = await Api.getToken();

        if (!token) {
          appContext.dispatch({ type: 'logout', from: 'getLoggedInUser' });
          await clearAllTokenState();
          return false;
        }

        try {
          const user = await Api.get('/members/' + token.user.ID);
          return user;
        } catch (error) {
          if (error?.status === 401 || error?.status === 403) {
            appContext.dispatch({ type: 'logout', from: 'getLoggedInUser:fetchMember' });
            await clearAllTokenState();
          }
          return false;
        }
      },
      getUserId: async () => {
        return await Api.getToken().then((token) => {
          if (!token)
            return false;
          else
            return token.user.ID;
        })
      },
      getSession: async () => {
        return await Api.getToken().then((token) => {
          if (!token)
            return false;
          else
            return token.user;
        })
      },
      loadOrganisations: (clientId) => {
        if (!clientId)
          return Promise.reject('No ID specified');
        return Api.get(`/clients/${clientId}/organisations`);
      },

    }
  }, [baseUrl, locale, appContext]);



  return Api;

}


export function useApiOld() {
  return useApi();
}
